import type { Href } from 'expo-router'

/**
 * Sidebar / navigation config — grouped exactly as the handoff chrome.
 * `route` values are expo-router paths inside the (portal) group.
 */

/** Href, narrowed to its plain-path string members (nav items never carry params) —
 *  stays a `string` for the pathname comparisons below, while still satisfying
 *  `router.push`/`Link href`'s typed-routes signature. */
export type NavRoute = Extract<Href, string>

export interface NavItem {
  key: string
  icon: string
  route: NavRoute
  /** Notification count. Injected live by the Sidebar (e.g. «Процессы» = open EDO tasks); not set statically. */
  badge?: number
  /** Section isn't built yet — sidebar badges it «Скоро», sinks it to the bottom of its group, and blocks navigation. */
  soon?: boolean
  /** Hidden from the sidebar entirely (route stays gated via `soon`). Flip to false to restore. */
  hidden?: boolean
}

export const NAV_MAIN: NavItem[] = [{ key: 'home', icon: 'house', route: '/home' }]

/**
 * Подписание через SIMA включается боевыми ключами, которых пока нет: без них ручки отвечают
 * 503, и пункт меню вёл бы на экран, где любая кнопка ошибается. Прячем флагом — тем же
 * приёмом, что «Договор» (`EXPO_PUBLIC_CONTRACT=0` в переменных прод-джобы): придут ключи —
 * снимается переменная в CI, код не трогаем.
 *
 * Значение подставляется НА СБОРКЕ (`babel-preset-expo` инлайнит `EXPO_PUBLIC_*`), поэтому
 * параметр: правило проверяется тестом, а подменить переменную в рантайме нельзя.
 */
export const signingEnabled = (
  flag: string | undefined = process.env.EXPO_PUBLIC_SIGNING,
): boolean => flag !== '0'

export const NAV_WORK: NavItem[] = [
  { key: 'processes', icon: 'file-text', route: '/processes' },
  ...(signingEnabled()
    ? [{ key: 'signing', icon: 'pen-line', route: '/signing' } satisfies NavItem]
    : []),
  { key: 'kb', icon: 'book-marked', route: '/kb' },
  { key: 'learning', icon: 'graduation-cap', route: '/learning', soon: true },
  { key: 'people', icon: 'users', route: '/people' },
  { key: 'org', icon: 'network', route: '/org' },
  { key: 'status', icon: 'user-check', route: '/absences' },
]

export const NAV_LIFE: NavItem[] = [
  { key: 'news', icon: 'newspaper', route: '/news' },
  { key: 'communities', icon: 'sparkles', route: '/communities', soon: true, hidden: true },
  { key: 'calendar', icon: 'calendar', route: '/calendar' },
]

export const NAV_BOTTOM: NavItem[] = [
  { key: 'settings', icon: 'settings', route: '/settings' },
  { key: 'help', icon: 'circle-help', route: '/help' },
]

/** Bottom-tab nav for phones. 5th tab «Ещё» opens the /more hub. */
export const NAV_TABS: NavItem[] = [
  { key: 'home', icon: 'house', route: '/home' },
  { key: 'news', icon: 'newspaper', route: '/news' },
  { key: 'processes', icon: 'file-text', route: '/processes' },
  { key: 'people', icon: 'users', route: '/people' },
  { key: 'more', icon: 'layout-grid', route: '/more' },
]

/**
 * «Ещё» hub — sections that lose their sidebar entry on phone. Derived from the
 * sidebar groups: everything not already a bottom tab and not `hidden`. Grouped
 * to mirror the sidebar (Работа / Жизнь / служебный блок без заголовка).
 */
const TAB_ROUTES = new Set(NAV_TABS.map((i) => i.route))
const inMore = (i: NavItem) => !i.hidden && !TAB_ROUTES.has(i.route)

export const NAV_MORE_GROUPS: { titleKey?: string; items: NavItem[] }[] = [
  { titleKey: 'nav.section.work', items: NAV_WORK.filter(inMore) },
  { titleKey: 'nav.section.life', items: NAV_LIFE.filter(inMore) },
  { items: NAV_BOTTOM.filter(inMore) },
]

/** Flat list of More-hosted routes — for the active-tab helper and tests. */
export const NAV_MORE: NavItem[] = NAV_MORE_GROUPS.flatMap((g) => g.items)

/** True when `pathname` is the hub or one of its hosted sections (keeps «Ещё» active). */
export const isMoreRoute = (pathname: string): boolean =>
  pathname === '/more' || NAV_MORE.some((i) => pathname === i.route || pathname.startsWith(i.route + '/'))

/** pathname → dictionary title key (for the topbar heading). */
export const ROUTE_TITLE: Record<string, string> = {
  '/': 'nav.home',
  '/home': 'nav.home',
  '/processes': 'nav.processes',
  '/processes/new': 'doc.new',
  '/processes/recruitment': 'proc.dashboard',
  '/signing': 'nav.signing',
  '/kb': 'nav.kb',
  '/learning': 'nav.learning',
  '/people': 'nav.people',
  '/org': 'nav.org',
  '/absences': 'nav.status',
  '/news': 'nav.news',
  '/communities': 'nav.communities',
  '/calendar': 'nav.calendar',
  '/settings': 'nav.settings',
  '/help': 'nav.help',
  '/more': 'nav.more',
  '/profile': 'top.profile',
}

/**
 * Routes for sections that aren't ready yet. Single source of truth = the
 * `soon` flags above. Consumed by the portal layout (redirect → /home) and the
 * sidebar / command palette (badge + skip). Flip a `soon: true` flag to gate a
 * section — everything else follows.
 */
const COMING_SOON_ROUTES: string[] = [...NAV_MAIN, ...NAV_WORK, ...NAV_LIFE, ...NAV_BOTTOM]
  .filter((i) => i.soon)
  .map((i) => i.route)

/** True when `pathname` belongs to a not-ready (coming-soon) section. */
export const isComingSoon = (pathname: string): boolean =>
  COMING_SOON_ROUTES.some((r) => pathname === r || pathname.startsWith(r + '/'))
